<template>
  <v-dialog :value="value" max-width="560" scrollable @input="$emit('input', $event)">
    <v-card light>
      <v-card-title class="subtitle-1 font-weight-bold pr-8">
        Gestures by typology
        <v-spacer />
        <v-btn icon small @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-subtitle v-if="analysisName" class="pb-0 pt-1 text-truncate">
        {{ analysisName }}
      </v-card-subtitle>

      <v-card-text class="pt-4">
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <v-alert v-else-if="errorMessage" outlined color="error" icon="mdi-alert" class="mb-0">
          {{ errorMessage }}
        </v-alert>

        <div v-else-if="!summary || rows.length === 0" class="text-center grey--text py-8">
          <v-icon large color="grey">mdi-gesture</v-icon>
          <p class="mt-2 mb-0">No gesture data available for this analysis.</p>
        </div>

        <template v-else>
          <div class="d-flex flex-wrap mb-3" style="gap: 8px">
            <v-chip small outlined>
              <v-icon small left>mdi-counter</v-icon>
              {{ summary.total_episodes }} detections
            </v-chip>
            <v-chip small outlined>
              <v-icon small left>mdi-speedometer</v-icon>
              {{ summary.mean_gestures_per_minute }} / min
            </v-chip>
            <v-chip v-if="summary.video_duration_seconds" small outlined>
              <v-icon small left>mdi-clock-outline</v-icon>
              {{ formatDuration(summary.video_duration_seconds) }}
            </v-chip>
          </div>

          <v-simple-table dense>
            <template #default>
              <thead>
                <tr>
                  <th class="text-left">Gesture</th>
                  <th class="text-right">Detections</th>
                  <th class="text-right">Alerts</th>
                  <th class="text-right">Discarded</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in rows" :key="row.name">
                  <td>{{ row.name }}</td>
                  <td class="text-right font-weight-medium">{{ row.episodes }}</td>
                  <td class="text-right">
                    <span
                      :class="
                        row.alerts > 0
                          ? 'orange--text text--darken-2 font-weight-medium'
                          : 'grey--text'
                      "
                    >
                      {{ row.alerts }}
                    </span>
                  </td>
                  <td class="text-right grey--text">{{ row.discarded }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </template>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn text @click="$emit('input', false)">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import GestureSummaryService from '@/services/GestureSummaryService';

export default {
  name: 'GestureTypologyDialog',

  props: {
    /** v-model: dialog visibility */
    value: {
      type: Boolean,
      default: false,
    },
    sourceType: {
      type: String,
      default: null, // 'interview' | 'manual_analysis'
    },
    sourceId: {
      type: [Number, String],
      default: null,
    },
    /** Optional display name shown under the title (e.g. the video name). */
    analysisName: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      loading: false,
      summary: null,
      errorMessage: '',
    };
  },

  computed: {
    /**
     * Table rows sorted by detection count (desc), then name. Prefers the
     * per-status breakdown (by_gesture_status); falls back to plain counts
     * (by_gesture_name) for responses from an older backend.
     */
    rows() {
      if (!this.summary) return [];
      const status = this.summary.by_gesture_status;
      if (status && typeof status === 'object' && Object.keys(status).length > 0) {
        return Object.entries(status)
          .map(([name, counts]) => ({
            name,
            episodes: Number(counts?.episodes) || 0,
            alerts: Number(counts?.alerts) || 0,
            discarded: Number(counts?.discarded) || 0,
          }))
          .sort((a, b) => b.episodes - a.episodes || a.name.localeCompare(b.name));
      }
      const byName = this.summary.by_gesture_name || {};
      return Object.entries(byName)
        .map(([name, count]) => ({
          name,
          episodes: Number(count) || 0,
          alerts: null,
          discarded: null,
        }))
        .sort((a, b) => b.episodes - a.episodes || a.name.localeCompare(b.name));
    },
  },

  watch: {
    value(open) {
      if (open) this.loadSummary();
    },
  },

  methods: {
    /** Always reloads on open: counts change after every re-analysis. */
    async loadSummary() {
      if (!this.sourceType || !this.sourceId) return;
      this.loading = true;
      this.errorMessage = '';
      this.summary = null;
      try {
        const response = await GestureSummaryService.get(this.sourceType, Number(this.sourceId));
        this.summary = response?.data?.data || null;
      } catch (error) {
        this.errorMessage = 'Could not load the gesture summary. Please try again later.';
      } finally {
        this.loading = false;
      }
    },

    formatDuration(seconds) {
      const total = Math.round(Number(seconds) || 0);
      const mins = Math.floor(total / 60);
      const secs = total % 60;
      return `${mins}:${String(secs).padStart(2, '0')} min`;
    },
  },
};
</script>
